/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { TabItemProps } from '@consumidor-positivo/aurora';
import { SHOffer } from '../../services/sortingHat';
import { useCreditTrack } from '../../hooks/useCreditTrack';

import { OfferListTab } from './components/Tab';
import { OfferSkeleton } from '../../components/OfferSkeleton';
import { OfferListProps } from '.';
import { benefitFilter, categoryFilter, rejectedOfferMap } from './data';
import { useOfferFilters } from '../../hooks/useOfferFilters';

type FilteredOffers = {
  [key: string]: SHOffer[];
};

const defaultTabs: TabItemProps[] = categoryFilter.map((filter) => {
  return {
    tab: filter.slug,
    title: filter.title,
    children: <OfferSkeleton />,
  };
});

export const useOffersList = ({ shDecisionList, SHLoading, user, OffersMiddleAd, SofiaInlineBanner, locationSlug }: OfferListProps) => {
  const { pathname } = useLocation();
  const { filterCategory, filterBenefit, persistFilter } = useOfferFilters();
  const { elementClickedEvent, productListViewedEvent, productListFilteredEvent } = useCreditTrack();

  const [tabs, setTabs] = useState<TabItemProps[]>(defaultTabs);
  const [initialCategoryTab, setInitialCategoryTab] = useState<string>();
  const [initialBenefitTab, setInitialBenefitTab] = useState<string>();
  const [filteredOffers, setFilteredOffers] = useState<FilteredOffers>();
  const [listViewed, setListViewed] = useState(false);
  const [error, setError] = useState({
    hasError: false,
    errorTitle: '',
    errorMessage: '',
  });

  const currentLocationSlug = locationSlug || 'offer-page';

  useEffect(() => {
    if (!filteredOffers) return;
    const visibleSlugs = tabs.map((t) => t.tab);
    const isCategoryVisible = visibleSlugs.includes(filterCategory ?? '');

    const safeCategoryTab = isCategoryVisible ? safeTabInitialization(filterCategory, filteredOffers) : 'all';
    const safeBenefitTab = safeTabInitialization(filterBenefit, filteredOffers);

    setInitialCategoryTab(safeCategoryTab);
    setInitialBenefitTab(safeBenefitTab);
  }, [filterCategory, filterBenefit, filteredOffers, tabs]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    function handleScroll() {
      const halfwayPoint = window.innerHeight / 2;
      const scrollPosition = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;

      if (scrollPosition < halfwayPoint) {
        setListViewed(true);
        window.removeEventListener('scroll', handleScroll);
      } else {
        setListViewed(false);
      }
    }
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    separateTabs(shDecisionList);
  }, [shDecisionList, initialCategoryTab, initialBenefitTab]);

  useEffect(() => {
    listViewed && sendEventListViewed();
  }, [listViewed]);

  useEffect(() => {
    checkErrorFromUrl();
  }, []);

  function separateTabs(offerList: SHOffer[]) {
    const categoryTabs: { [key: string]: SHOffer[] } = {
      all: offerList,
      loans: [],
      cards: [],
      consorcio: [],
      account: [],
    };

    const benefitTabs: { [key: string]: { [key: string]: SHOffer[] } } = {
      cards: {
        pre_approved: [],
        zero_annuity: [],
        miles: [],
        cashback: [],
        all: [],
      },
      loans: { personal: [], assurance: [], consigned: [], pj: [], all: [] },
      consorcio: { all: [] },
    };

    for (const offer of offerList) {
      const category = offer?.content?.category;
      const benefits = offer?.content?.benefit_category || [];
      const isPreApproved = offer?.generalInfo?.preApproved;

      if (category === 'insurance' || category === 'collections') continue;
      categoryTabs?.[category]?.push(offer);

      benefitTabs[category]?.['all']?.push(offer);

      if (category === 'cards' && isPreApproved) {
        benefitTabs?.[category]?.['pre_approved']?.push(offer);
      }

      benefits.forEach((benefit) => {
        benefitTabs[category]?.[benefit]?.push(offer);
      });
    }

    const benefitsTabs = Object.entries(benefitFilter).map(([key, value]) => {
      return value.map((filter) => {
        return {
          tab: filter.slug,
          title: filter.title,
          icon: filter.icon,
          list: benefitTabs[key]?.[filter.slug],
        };
      });
    });

    const benefitsTabsList: any = {
      cards: benefitsTabs[0],
      loans: benefitsTabs[1],
    };

    const categoriesWithOffers = categoryFilter
      .filter((c) => c.slug !== 'all' && categoryTabs[c.slug]?.length > 0)
      .map((c) => c.slug);
    const visibleCategorySlugs = categoriesWithOffers.length <= 1 ? ['all'] : [...categoriesWithOffers, 'all'];

    const updatedTabs = defaultTabs
      .filter((tab) => visibleCategorySlugs.includes(tab.tab))
      .map((oldTab) => ({
        ...oldTab,
        children: (
          <OfferListTab
            user={user}
            loading={SHLoading}
            location={currentLocationSlug}
            offers={categoryTabs[oldTab.tab]}
            offerForBenefits={benefitsTabsList[oldTab.tab]}
            benefitsFilterText={categoryFilter.find((item) => item.slug === oldTab.tab)?.benefitFilterText}
            onClickBenefitsTabs={handleFilterChange}
            initalBenefitTab={initialBenefitTab}
            OffersMiddleAd={OffersMiddleAd}
            SofiaInlineBanner={SofiaInlineBanner}
          />
        ),
      }));
    setFilteredOffers({ ...categoryTabs, ...benefitTabs['cards'], ...benefitTabs['loans'] });
    setTabs(updatedTabs);
  }

  async function handleFilterChange(value: string, filterType = 'category') {
    const filters = [...categoryFilter, ...benefitFilter.cards, ...benefitFilter.loans];
    const currentFilter = filters.find((filter) => filter.slug === value);

    elementClickedEvent({
      elementType: 'tab',
      location: `${currentLocationSlug}-${filterType}-tab`,
      text: currentFilter?.title,
      name: currentFilter?.slug,
    });

    productListFilteredEvent({
      currentCategory: value,
      listId: `${currentLocationSlug}-${filterType}-tab`,
      filterType,
      filteredOffers: filteredOffers?.[value] || [],
    });

    persistFilter(value, filterType);
  }

  function sendEventListViewed() {
    if (!filteredOffers) return;

    const listId = `${currentLocationSlug}-${initialCategoryTab}`;
    const initialCategory = initialCategoryTab || 'all';
    productListViewedEvent({
      listId,
      initialCategory,
      products: filteredOffers?.[initialCategory],
    });
  }

  function checkErrorFromUrl() {
    const url = window.location.href;
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    const rejectedSlug = params.get('falha-no-processamento');

    if (rejectedSlug) {
      const defaultMessage = {
        title: 'Ops! Algo deu errado.',
        message: 'Ocorreu um erro ao processar sua solicitação. Tente novamente mais tarde.',
      };

      const rejectOffer = rejectedOfferMap[rejectedSlug as keyof typeof rejectedOfferMap];
      const safeRejectOffer = rejectOffer || defaultMessage;

      setError({
        hasError: true,
        errorTitle: safeRejectOffer.title,
        errorMessage: safeRejectOffer.message,
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function safeTabInitialization(tab: string | undefined, organizedOffers: FilteredOffers) {
    const isValidTab = Object.keys(organizedOffers || {}).includes(tab || '');
    if (!isValidTab) return 'all';

    if (organizedOffers[tab || '']?.length === 0) return 'all';
    return tab;
  }

  return {
    tabs,
    error,
    initialTab: initialCategoryTab,
    handleFilterChange,
  };
};
